import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ComplianceDocument, Contact } from '@/lib/contract';

/**
 * One owner for the `/vendor-profile` response.
 *
 * Two screens fetched this under the same cache key with two different, incompatible declared
 * types — `intake: {bucket, access, rotated} | null` in one and `{ intake?: { bucket?: string } }`
 * in the other. React Query stores one entry per key, so whichever screen mounted first decided
 * what the other actually received, and TypeScript could not see the conflict because each screen
 * asserted the shape at its own call site.
 *
 * The key, the endpoint and the type belong together. This is where they live.
 */
export interface VendorProfilePayload {
  capabilities: { label: string; values: { text: string; pending?: boolean }[] }[];
  compliance_documents: ComplianceDocument[];
  contacts: Contact[];
  /** Null until intake credentials have been issued: an absence, not an empty bucket. */
  intake: { bucket: string; access: string; rotated: string } | null;
}

export const vendorProfileKey = ['vendor', 'profile-detail'] as const;

export function useVendorProfile() {
  return useQuery({
    queryKey: vendorProfileKey,
    queryFn: async () => {
      const raw = (await api<Partial<VendorProfilePayload>>('/vendor-profile')) ?? {};
      // Filled at the boundary rather than trusted: both screens map over these arrays directly,
      // and this endpoint bypassed normalize.ts entirely.
      return {
        capabilities: Array.isArray(raw.capabilities) ? raw.capabilities : [],
        compliance_documents: Array.isArray(raw.compliance_documents) ? raw.compliance_documents : [],
        contacts: Array.isArray(raw.contacts) ? raw.contacts : [],
        intake: raw.intake ?? null
      } satisfies VendorProfilePayload;
    }
  });
}
